import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const sortParam = searchParams.get('sort') || 'latest';

  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState(sortParam);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      setCurrentPage(1);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory, currentPage]);

  const categoryName = categories.find(c => c.slug === activeCategory)?.name || '全部';

  let filteredComics = activeCategory === 'all'
    ? comics
    : comics.filter(c => c.categorySlug === activeCategory);

  if (searchQuery) {
    filteredComics = filteredComics.filter(c =>
      c.title.includes(searchQuery) ||
      c.author.includes(searchQuery) ||
      c.tags.some(t => t.includes(searchQuery))
    );
  }

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'hot') return b.views - a.views;
    if (sortBy === 'rating') return b.rating - a.rating;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const totalPages = Math.ceil(sortedComics.length / pageSize);
  const paginatedComics = sortedComics.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hahamanhua.com' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `https://hahamanhua.com/category/${activeCategory}` },
    ],
  };

  const getSortLabel = (s: string) => {
    if (s === 'latest') return '最新';
    if (s === 'hot') return '最热';
    if (s === 'rating') return '评分';
    return '最新';
  };

  const breadcrumbs = [
    { label: '首页', path: '/' },
    { label: categoryName, path: `/category/${activeCategory}` },
  ];

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <div className="bg-white border-b border-foreground-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="面包屑导航">
            {breadcrumbs.map((crumb, idx) => (
              <span key={crumb.path} className="flex items-center gap-2">
                {idx > 0 && <i className="ri-arrow-right-s-line"></i>}
                {idx < breadcrumbs.length - 1 ? (
                  <Link to={crumb.path} className="hover:text-primary-600 transition-colors">{crumb.label}</Link>
                ) : (
                  <span className="text-foreground-900 font-medium">{crumb.label}</span>
                )}
              </span>
            ))}
            {searchQuery && (
              <span className="flex items-center gap-2">
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700">搜索: {searchQuery}</span>
              </span>
            )}
          </nav>

          <h1 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4">
            {searchQuery ? `搜索结果: "${searchQuery}"` : `${categoryName}漫画`}
          </h1>

          <div className="flex items-center gap-2 flex-wrap mb-1">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${activeCategory === cat.slug ? 'bg-primary-500 text-white font-medium' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-foreground-500">
            共 <strong className="text-foreground-900">{sortedComics.length}</strong> 部漫画
          </p>
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
            {['latest', 'hot', 'rating'].map((s) => (
              <button
                key={s}
                onClick={() => { setSortBy(s); setCurrentPage(1); }}
                className={`px-4 py-1.5 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${sortBy === s ? 'bg-white text-foreground-900 font-medium shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
              >
                {getSortLabel(s)}
              </button>
            ))}
          </div>
        </div>

        {paginatedComics.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">暂无相关漫画，试试其他分类吧</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {paginatedComics.map((comic: Comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 rounded-lg flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors cursor-pointer ${p === currentPage ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 rounded-lg flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}